'use client';

import { Hide, HStack, Show, SimpleGrid } from '@chakra-ui/react';
import {
  isPublicSpeculator,
  type NestedSpeculator,
  type PublicNestedSpeculator,
} from '@habity/types';
import { NoResults } from '~/components/ui/grids/components/NoResults';
import { getSpeculatorPath } from '@habity/services';
import { useGridFullRows } from '~/hooks/useGridFullRows';
import { SpeculatorStatCard } from '~/components/buyers/cards/components/Stat';
import { useSearchParams } from 'next/navigation';
import { useHomesStore } from '~/store';
import { GridSpeculatorCard } from '~/components/buyers/cards/Grid';

interface SpeculatorsGridViewProps {
  speculators?: Array<NestedSpeculator | PublicNestedSpeculator> | null;
  loading: boolean;
  hasMore: boolean;
}

export const SpeculatorsGridView = ({
  speculators: realSpeculators,
  loading,
  hasMore,
}: SpeculatorsGridViewProps) => {
  const count = useHomesStore((state) => state.count);

  const speculators: Array<NestedSpeculator | PublicNestedSpeculator> =
    loading && realSpeculators && realSpeculators.length === 0
      ? []
      : realSpeculators && realSpeculators.length > 0
        ? realSpeculators
        : [];

  // Use counts from the store (populated by list/page.tsx)
  const user_count = count.speculatorsUnique || undefined;
  const viewing_count = count.speculatorsViewing || undefined;
  const bid_count = count.speculatorsBidding || undefined;

  const searchParams = useSearchParams();

  const isInterestFilteredSearch = !!searchParams?.get('interests')?.length;

  // Construct stat labels
  // Different wording when the interest filters are active
  const userCountLabel = isInterestFilteredSearch
    ? 'spekulanter matchar den aktuella sökningen'
    : 'spekulanter letar bostad via oss just nu';
  const viewingCountLabel = isInterestFilteredSearch
    ? 'av dessa har nyligen bokat visning'
    : 'spekulanter har nyligen bokat visning';
  const bidCountLabel = isInterestFilteredSearch
    ? 'av dessa deltar just nu i budgivningar'
    : 'spekulanter deltar just nu i budgivningar';

  const { gridRef, visibleItems: visibleSpeculators } = useGridFullRows(
    speculators,
    hasMore
  );

  if (!visibleSpeculators || !visibleSpeculators.length) {
    return <NoResults tab="speculators" />;
  }

  return (
    <SimpleGrid
      ref={gridRef}
      columns={{ mobile: 1, tablet: 2, desktop: 3 }}
      rowGap="10"
      columnGap="5"
      pt="0"
      pb={{ mobile: '12', desktop: '24' }}
    >
      {/* Stats */}
      <SpeculatorStatCard value={user_count} label={userCountLabel} />

      <Hide below="desktop">
        <SpeculatorStatCard value={viewing_count} label={viewingCountLabel} />
        <SpeculatorStatCard value={bid_count} label={bidCountLabel} />
      </Hide>

      <Show below="desktop">
        <HStack>
          <SpeculatorStatCard value={viewing_count} label={viewingCountLabel} />
          <SpeculatorStatCard value={bid_count} label={bidCountLabel} />
        </HStack>
      </Show>

      {visibleSpeculators?.map((speculator, index) => {
        // Preserve current showBuyerDetails mode when navigating to profile
        const baseHref = getSpeculatorPath(speculator?.search);
        const currentShowBuyerDetails =
          searchParams?.get('showBuyerDetails') === 'true';
        const hrefWithDetailView =
          currentShowBuyerDetails && !isPublicSpeculator(speculator)
            ? `${baseHref}?showBuyerDetails=true`
            : baseHref;

        return (
          <GridSpeculatorCard
            key={index}
            speculator={speculator}
            href={hrefWithDetailView}
            isLoading={speculator?.loading || false}
          />
        );
      })}
    </SimpleGrid>
  );
};
